import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, Signal, WritableSignal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';

import { FormulaBuilderComponent } from './components/formula-builder/formula-builder.component';
import { FormulaEditorComponent } from './components/formula-editor/formula-editor.component';
import { FormulaEvaluationResultComponent } from './components/formula-evaluation-result/formula-evaluation-result.component';
import { FormulaHelpGuideComponent } from './components/formula-help-guide/formula-help-guide.component';
import { FormulaPreviewComponent } from './components/formula-preview/formula-preview.component';
import { FormulaQueryBuilderMode } from './models/formula-query-builder.model';
import { FormulaEvaluationResult, FormulaValidationResult } from './models/formula-result.model';
import { DEFAULT_FORMULA_EDITOR_CONFIG, FormulaEditorConfig } from './utils/formula-editor-config';
import { evaluateFormula } from './utils/formula-utils';

@Component({
  selector: 'iot-platform-ui-formula-query-builder',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    FormulaEditorComponent,
    FormulaBuilderComponent,
    FormulaPreviewComponent,
    FormulaEvaluationResultComponent,
    FormulaHelpGuideComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './formula-query-builder.component.html',
  styleUrls: ['./formula-query-builder.component.scss']
})
export class FormulaQueryBuilderComponent {
  /** Available variable names (already aliased) for autocomplete, validation and preview */
  readonly variables = input.required<string[]>();
  /** Current formula text (controlled from the parent) */
  readonly formula = input<string>('');
  readonly showToggleMode = input<boolean>(false);
  /** Editor configuration (operators / functions restrictions) */
  readonly config = input<FormulaEditorConfig>(DEFAULT_FORMULA_EDITOR_CONFIG);
  /** Initial editor mode (text | visual). The component owns the live mode signal afterwards. */
  readonly initialMode = input<FormulaQueryBuilderMode>('text');
  /** Optional maximum allowed formula length in characters */
  readonly maxLength = input<number | undefined>(undefined);
  /**
   * Map of variable alias → current numeric value.
   * When provided, the component evaluates the formula on every change
   * and emits the result via `evaluationChange`.
   */
  readonly variableValues = input<Record<string, number | null>>({});

  /** Emits the formula string on every change (from text or visual mode) */
  readonly formulaChange = output<string>();
  /** Emits the validation result coming from the text editor */
  readonly validationChange = output<FormulaValidationResult>();
  /** Emits when the user toggles the editor mode */
  readonly modeChange = output<FormulaQueryBuilderMode>();
  /** Emits the formula evaluation result whenever the formula or variable values change */
  readonly evaluationChange = output<FormulaEvaluationResult>();

  readonly mode: WritableSignal<FormulaQueryBuilderMode> = signal<FormulaQueryBuilderMode>('text');
  readonly currentEvalResult: WritableSignal<FormulaEvaluationResult | null> = signal(null);

  /** True while the cursor hovers over the info icon button */
  readonly buttonHovered: WritableSignal<boolean> = signal(false);
  /** True while the cursor is inside the overlay panel */
  readonly overlayHovered: WritableSignal<boolean> = signal(false);
  /** Help overlay is open when either the button or the panel is hovered */
  readonly showHelpGuide: Signal<boolean> = computed(() => this.buttonHovered() || this.overlayHovered());

  readonly validationResult: WritableSignal<FormulaValidationResult | null> = signal(null);
  /**
   * CDK connected overlay anchor positions.
   *
   * Priority order:
   *  1. bottom-end   — preferred on wide screens (right-edge aligned, opens below)
   *  2. top-end      — fallback when there is no space below (right-edge aligned, opens above)
   *  3. bottom-start — small-screen fallback: left edge anchored to origin left edge (pulled right)
   *  4. top-start    — small-screen + no space below
   *
   * Combined with cdkConnectedOverlayFlexibleDimensions + viewportMargin="8", CDK picks
   * the best fitting position and shrinks the overlay if needed so it stays fully visible
   * on narrow viewports without overflowing the screen edges.
   */
  readonly helpGuidePositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  constructor() {
    effect(() => {
      this.mode.set(this.initialMode());
    });

    effect(() => {
      const expression = this.formula();
      const variableValues = this.variableValues();
      if (!expression?.trim()) {
        this.currentEvalResult.set(null);
        return;
      }
      const evalResult = evaluateFormula(expression, variableValues);
      this.currentEvalResult.set(evalResult);
      this.evaluationChange.emit(evalResult);
    });
  }

  /** Resets hover state when the CDK overlay is detached externally (e.g. scroll, focus loss) */
  onHelpGuideDetach(): void {
    this.buttonHovered.set(false);
    this.overlayHovered.set(false);
  }

  onModeChange(newMode: FormulaQueryBuilderMode): void {
    this.mode.set(newMode);
    this.modeChange.emit(newMode);
  }

  onFormulaChange(formula: string): void {
    this.formulaChange.emit(formula ?? '');
  }

  onValidationChange(result: FormulaValidationResult): void {
    this.validationResult.set(result);
    this.validationChange.emit(result);
  }
}
